import React from "react";
import { Droplet } from "lucide-react";

export function StatusDot({ status }) {
  const map = {
    running: "bg-emerald-500 status-pulse-green",
    working: "bg-emerald-500 status-pulse-green",
    online: "bg-emerald-500 status-pulse-green",
    stopped: "bg-red-500",
    offline: "bg-red-500",
    fault: "bg-yellow-500",
    alarm: "bg-red-500 status-pulse-red",
    active: "bg-emerald-500 status-pulse-green",
  };
  return <span className={`inline-block w-3 h-3 rounded-full ${map[status?.toLowerCase()] || "bg-slate-500"}`} />;
}

export function StatusCircle({ status, label, subtitle }) {
  const isRun = ["running", "working", "online", "active"].includes(status?.toLowerCase());
  const isFault = status?.toLowerCase() === "fault";
  const isUnknown = !status || status?.toLowerCase() === "unknown";
  const color = isUnknown
    ? "from-slate-300 to-slate-400 shadow-slate-300/40"
    : isRun
    ? "from-emerald-400 to-emerald-600 shadow-emerald-500/40"
    : isFault
    ? "from-yellow-400 to-yellow-600 shadow-yellow-500/40"
    : "from-red-500 to-red-700 shadow-red-500/40";
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${color} shadow-lg flex items-center justify-center ${isRun && !isUnknown ? "status-pulse-green" : ""}`}>
        <div className="w-3 h-3 rounded-full bg-white/90" />
      </div>
      <div className="text-center">
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        <div className="text-xs uppercase tracking-wider text-gray-500">{subtitle || status}</div>
      </div>
    </div>
  );
}

export function PressureGauge({ value, min = 0, max = 10, threshold = 3.5, label, critical }) {
  const pct = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  const isCritical = value < threshold;
  const color = isCritical ? "#ef4444" : value < threshold * 1.3 ? "#f59e0b" : "#22c55e";
  // semicircle gauge
  const radius = 72;
  const circumference = Math.PI * radius;
  const stroke = (pct / 100) * circumference;

  return (
    <div className={`relative ${critical && isCritical ? "banner-blink" : ""}`}>
      <svg viewBox="0 0 200 120" className="w-full">
        <path
          d="M 20 110 A 80 80 0 0 1 180 110"
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <path
          d="M 20 110 A 80 80 0 0 1 180 110"
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${stroke} ${circumference}`}
          style={{ transition: "stroke-dasharray 0.6s, stroke 0.4s" }}
        />
        {/* threshold marker */}
        <g transform={`rotate(${-180 + (threshold / max) * 180}, 100, 110)`}>
          <line x1="100" y1="30" x2="100" y2="42" stroke="#ff3366" strokeWidth="2" />
        </g>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
        <div className="text-3xl font-extrabold text-gray-900 leading-none">{value?.toFixed(2)}</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{label || "Bar"}</div>
        <div className={`text-[10px] mt-1 font-semibold ${isCritical ? "text-red-400" : "text-emerald-400"}`}>
          {isCritical ? `BELOW GR MIN ${threshold} BAR` : `≥ ${threshold} BAR ✓`}
        </div>
      </div>
    </div>
  );
}

export function VerticalGauge({ value, label, sublabel, thresholds = { low: 25, mid: 50 } }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const color = pct < thresholds.low ? "#ef4444" : pct < thresholds.mid ? "#f59e0b" : "#22c55e";
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-14 h-40 bg-gray-50 rounded-md border border-gray-200 overflow-hidden">
        <div
          className="absolute bottom-0 left-0 right-0 transition-all duration-700"
          style={{ height: `${pct}%`, background: `linear-gradient(180deg, ${color}cc, ${color}88)` }}
        >
          <div className="absolute top-0 left-0 right-0 h-2 bg-white/30 tank-wave" />
        </div>
        {[25, 50, 75].map((m) => (
          <div key={m} className="absolute left-0 right-0 border-t border-gray-200" style={{ bottom: `${m}%` }} />
        ))}
      </div>
      <div className="text-center">
        <div className="text-xl font-bold text-gray-900">{pct.toFixed(0)}<span className="text-xs text-gray-500">%</span></div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        {sublabel && <div className="text-[10px] text-gray-500 mt-0.5">{sublabel}</div>}
      </div>
    </div>
  );
}

export function TankGraphic({ value, label, liters, capacityLiters }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const color = pct < 30 ? "#ef4444" : pct < 60 ? "#f59e0b" : "#4fd1ff";
  // Filled volume alone (e.g. "1 L") reads as broken/empty for a small tank at a low fill —
  // pairing it with the tank's own capacity (e.g. "1 / 20 L") makes clear it's a real, small
  // number relative to a real, small tank, not a missing/zeroed reading. Both sides come from
  // the live reading and the asset's own configured capacity — nothing here is a fixed default.
  const volumeLabel = liters != null
    ? (capacityLiters ? `${liters.toLocaleString()} / ${capacityLiters.toLocaleString()} L` : `${liters.toLocaleString()} L`)
    : "";
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-32 rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50">
        <Droplet className="absolute top-2 left-1/2 -translate-x-1/2 w-4 h-4 text-gray-500" />
        <div
          className="absolute bottom-0 left-0 right-0 transition-all duration-700"
          style={{ height: `${pct}%`, background: `linear-gradient(180deg, ${color}aa, ${color})` }}
        >
          <div className="absolute top-0 left-[-10%] right-[-10%] h-3 bg-white/40 rounded-full tank-wave" />
        </div>
      </div>
      <div>
        <div className="text-2xl font-extrabold text-gray-900">{pct.toFixed(0)}%</div>
        <div className="text-xs text-gray-500">{volumeLabel}</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
      </div>
    </div>
  );
}
